import { colors } from 'ui/theme';
import { MutatedExperimentVariant } from 'modules/experiments/takeover/types';

import { UpdateExperimentSettingsFormFieldNames } from '../constants/updateExperimentSettings';
import { calculateRatePercentage } from './common';
import { AudienceBreakdownMetric, AudienceBreakdownSection, TestStrategy } from '../types';

type GetSectionValuesProps = {
  sendSize: number | null;
  holdoutGroupPercent: number;
  testGroupPercent: number;
  hasHoldoutGroup: boolean;
  hasOptimizedGroup: boolean;
};

type GetTestGroupSizeProps = {
  testStrategy: TestStrategy;
  testGroupSize: number;
  hasHoldoutGroup: boolean;
  holdoutGroupSize: number;
};

const getSectionValues = ({
  sendSize,
  holdoutGroupPercent,
  testGroupPercent,
  hasHoldoutGroup,
  hasOptimizedGroup,
}: GetSectionValuesProps) => {
  let optimizedGroupSize = sendSize || 0;
  let optimizedGroupPercent = 100;
  const { rate: holdoutRate } = calculateRatePercentage({ count: holdoutGroupPercent, total: 100 });
  const { rate: testRate } = calculateRatePercentage({
    count: testGroupPercent,
    total: 100,
  });
  const holdoutSize = sendSize ? Math.round(sendSize * holdoutRate) : 0;
  if (hasHoldoutGroup) {
    optimizedGroupSize -= holdoutSize;
    optimizedGroupPercent -= holdoutGroupPercent;
  }
  const testSize = sendSize ? Math.round(sendSize * testRate) : 0;
  if (hasOptimizedGroup) {
    optimizedGroupSize -= testSize;
    optimizedGroupPercent -= testGroupPercent;
  }
  return {
    optimizedGroupSize,
    optimizedGroupPercent,
    holdoutRate,
    testRate,
    holdoutSize,
    testSize,
  };
};

type GetGroupMetricsProps = {
  groupName: AudienceBreakdownSection;
  groupSize: number;
  groupPercent: number;
  variantList: MutatedExperimentVariant[];
  customStyling?: SectionStyles | undefined;
};

type SectionStyles = {
  alphabet: string;
  color: string;
  backgroundColor: string;
  borderColor: string;
};

const getGroupMetrics = ({
  groupName,
  groupSize,
  groupPercent,
  variantList,
  customStyling = undefined,
}: GetGroupMetricsProps): AudienceBreakdownMetric => {
  const subsections = customStyling
    ? [
        {
          alphabet: customStyling.alphabet,
          color: customStyling.color,
          backgroundColor: customStyling.backgroundColor,
          borderColor: customStyling.borderColor,
          percent: groupPercent / variantList.length,
          sends: groupSize / variantList.length,
        },
      ]
    : variantList.map((variant) => ({
        alphabet: variant.alphabet,
        color: variant.textColor,
        backgroundColor: variant.bgColor,
        borderColor: variant.borderColor,
        percent: groupPercent / variantList.length,
        sends: groupSize / variantList.length,
      }));
  return {
    label: groupName,
    percent: groupPercent,
    total: groupSize,
    subsections,
  };
};

type GetAudienceBreakdownMetricsBySectionProps = {
  sendSize: number | null | undefined;
  hasHoldoutGroup: boolean;
  holdoutGroupPercent: number;
  hasOptimizedGroup: boolean;
  testGroupPercent: number;
  variantList: MutatedExperimentVariant[];
};

export const getAudienceBreakdownMetricsBySection = ({
  sendSize = 0,
  hasHoldoutGroup = false,
  holdoutGroupPercent = 0,
  hasOptimizedGroup = false,
  testGroupPercent = 0,
  variantList = [],
}: GetAudienceBreakdownMetricsBySectionProps): AudienceBreakdownMetric[] => {
  const { optimizedGroupSize, optimizedGroupPercent, holdoutSize, testSize } = getSectionValues({
    sendSize,
    holdoutGroupPercent,
    testGroupPercent,
    hasHoldoutGroup,
    hasOptimizedGroup,
  });

  const testSection = getGroupMetrics({
    groupName: AudienceBreakdownSection.TEST,
    groupSize: testSize,
    groupPercent: testGroupPercent,
    variantList,
  });

  const holdoutSectionArray = hasHoldoutGroup
    ? [
        getGroupMetrics({
          groupName: AudienceBreakdownSection.HOLDOUT,
          groupSize: holdoutSize,
          groupPercent: holdoutGroupPercent,
          variantList,
          customStyling: {
            alphabet: ' ',
            color: '',
            backgroundColor: colors.grey10,
            borderColor: colors.grey15,
          },
        }),
      ]
    : [];

  const optimizedSectionArray = hasOptimizedGroup
    ? [
        getGroupMetrics({
          groupName: AudienceBreakdownSection.OPTIMIZED,
          groupSize: optimizedGroupSize,
          groupPercent: optimizedGroupPercent,
          variantList,
          customStyling: {
            alphabet: ' ',
            color: '',
            backgroundColor: colors.green50,
            borderColor: colors.green100,
          },
        }),
      ]
    : [];

  return [...holdoutSectionArray, testSection, ...optimizedSectionArray];
};

export const getHasError = (
  formField: UpdateExperimentSettingsFormFieldNames[],
  errors: Record<string, any>,
) => formField.reduce((acc, item) => acc || !!errors[item], false);

export const hasOptimizedGroup = (testStrategy: TestStrategy) =>
  testStrategy === TestStrategy.SUBSET_USERS;

export const getTestGroupSize = ({
  testStrategy,
  testGroupSize,
  hasHoldoutGroup,
  holdoutGroupSize,
}: GetTestGroupSizeProps) => {
  let testSize = 100;

  switch (true) {
    case hasOptimizedGroup(testStrategy):
      testSize = testGroupSize;
      break;
    case hasHoldoutGroup:
      testSize = 100 - holdoutGroupSize;
      break;

    default:
  }
  return testSize;
};
